import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { Delete, Get, Post, Put, uploadFile } from "../api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/Spinner";
import { ColorPickerCustom } from "./ColorPickerCustom";

function getAssignedModifiers(product) {
  return (product?.mods || [])
    .map((item) => {
      const collection = item.mod_collection || {};
      const collectionId = item.mod_collection_id ?? collection.id;

      if (collectionId == null) return null;

      return {
        collectionId: String(collectionId),
        lineId: item.id == null ? null : String(item.id),
        name: collection.name || "Modificador",
      };
    })
    .filter(Boolean);
}

/**
 * El backend devuelve `error` para 4xx/5xx y `errors` con el detalle de cada
 * campo en los 422. Sin esto el toast acababa siempre en el texto genérico y no
 * había forma de saber qué había fallado al guardar.
 */
function describeSaveError(response) {
  const fieldErrors = response?.errors
    ? Object.values(response.errors).flat().filter(Boolean)
    : [];

  if (fieldErrors.length) return fieldErrors.join(" · ");
  return response?.error || response?.message || "No se pudo guardar el producto";
}

export function ProductEditor({ open, setOpen, product, sectionId, refetch }) {
  const isEditing = Boolean(product);
  const formId = useId();
  const imageInputRef = useRef(null);
  const [color, setColor] = useState(product?.color || "#F5F5F5");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState([]);
  const [pendingModifier, setPendingModifier] = useState("");
  const [saving, setSaving] = useState(false);

  const shopId = localStorage.getItem("shop_id");
  const exerciseId = localStorage.getItem("exercise_id");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: product?.name || "",
      price: product?.price ?? "",
      tax_id: product?.tax_id == null ? "" : String(product.tax_id),
      sections_id:
        product?.sections_id == null
          ? sectionId == null
            ? ""
            : String(sectionId)
          : String(product.sections_id),
    },
  });

  const taxesQuery = useQuery({
    queryKey: ["product-editor", "taxes", shopId, exerciseId],
    queryFn: () =>
      Get({
        request: `/tax?shop_id=${shopId}&exercise_id=${exerciseId}`,
      }),
    enabled: open,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const sectionsQuery = useQuery({
    queryKey: ["product-editor", "sections", shopId, exerciseId],
    queryFn: () =>
      Get({
        request: `/sections?shop_id=${shopId}&exercise_id=${exerciseId}`,
      }),
    enabled: open,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const modifiersQuery = useQuery({
    queryKey: ["product-editor", "modifiers", shopId, exerciseId],
    queryFn: () =>
      Get({
        request: `/mods?shop_id=${shopId}&exercise_id=${exerciseId}`,
      }),
    enabled: open,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isLoading =
    open &&
    (taxesQuery.isLoading ||
      sectionsQuery.isLoading ||
      modifiersQuery.isLoading);

  const taxes = useMemo(() => taxesQuery.data || [], [taxesQuery.data]);
  const sections = useMemo(
    () => sectionsQuery.data || [],
    [sectionsQuery.data],
  );
  const modifiers = useMemo(
    () => modifiersQuery.data || [],
    [modifiersQuery.data],
  );
  const selectedTaxId = watch("tax_id");
  const selectedSectionId = watch("sections_id");
  const productName = watch("name", "");
  const productPrice = watch("price", "");
  const imageSource = imagePreview || product?.image;
  const numericPrice = Number(productPrice);
  const previewPrice = Number.isFinite(numericPrice) ? numericPrice : 0;

  useEffect(() => {
    if (!open) return;

    reset({
      name: product?.name || "",
      price: product?.price ?? "",
      tax_id: product?.tax_id == null ? "" : String(product.tax_id),
      sections_id:
        product?.sections_id == null
          ? sectionId == null
            ? ""
            : String(sectionId)
          : String(product.sections_id),
    });
    setColor(product?.color || "#F5F5F5");
    setImageFile(null);
    setSelectedModifiers(getAssignedModifiers(product));
    setPendingModifier("");

    if (imageInputRef.current) imageInputRef.current.value = "";
  }, [open, product, sectionId, reset]);

  useEffect(() => {
    if (imageFile) {
      const previewUrl = URL.createObjectURL(imageFile);
      setImagePreview(previewUrl);
      return () => URL.revokeObjectURL(previewUrl);
    }

    setImagePreview(null);
    return undefined;
  }, [imageFile]);

  useEffect(() => {
    if (!open || selectedTaxId || !taxes.length) return;
    setValue("tax_id", String(taxes[0].id));
  }, [open, selectedTaxId, setValue, taxes]);

  useEffect(() => {
    if (!open || selectedSectionId || !sections.length) return;
    setValue("sections_id", String(sections[0].id));
  }, [open, selectedSectionId, setValue, sections]);

  const availableModifiers = modifiers.filter(
    (modifier) =>
      !selectedModifiers.some(
        (selected) => selected.collectionId === String(modifier.id),
      ),
  );

  function handleImageChange(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona un archivo de imagen");
      event.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no puede superar 5 MB");
      event.target.value = "";
      return;
    }

    setImageFile(file);
  }

  function addModifier() {
    const modifier = modifiers.find(
      (item) => String(item.id) === String(pendingModifier),
    );
    if (!modifier) return;

    setSelectedModifiers((current) => [
      ...current,
      {
        collectionId: String(modifier.id),
        lineId: null,
        name: modifier.name,
      },
    ]);
    setPendingModifier("");
  }

  async function syncModifiers(savedProductId) {
    const originalModifiers = getAssignedModifiers(product);
    const originalIds = new Set(
      originalModifiers.map((modifier) => modifier.collectionId),
    );
    const selectedIds = new Set(
      selectedModifiers.map((modifier) => modifier.collectionId),
    );
    const additions = selectedModifiers.filter(
      (modifier) => !originalIds.has(modifier.collectionId),
    );
    const removals = originalModifiers.filter(
      (modifier) => !selectedIds.has(modifier.collectionId),
    );

    for (const modifier of additions) {
      const response = await Put({
        request: `/products/${savedProductId}/mods?shop_id=${shopId}&exercise_id=${exerciseId}`,
        data: { mod_collection_id: modifier.collectionId },
      });
      if (response?.error) throw new Error(response.error);
    }

    for (const modifier of removals) {
      if (!modifier.lineId) continue;
      await Delete({
        request: `/products/${savedProductId}/mods/${modifier.lineId}?shop_id=${shopId}&exercise_id=${exerciseId}`,
      });
    }
  }

  const onSubmit = handleSubmit(async (values) => {
    const name = String(values.name || "").trim();
    const price = Number(values.price);

    if (!name) {
      setError("name", { type: "manual", message: "Escribe un nombre" });
      return;
    }

    if (!Number.isFinite(price) || price < 0) {
      setError("price", {
        type: "manual",
        message: "Introduce un precio válido",
      });
      return;
    }

    if (!values.sections_id) {
      setError("sections_id", {
        type: "manual",
        message: "Selecciona una sección",
      });
      return;
    }

    if (!values.tax_id) {
      setError("tax_id", {
        type: "manual",
        message: "Selecciona un impuesto",
      });
      return;
    }

    setSaving(true);
    const productData = {
      name,
      price,
      tax_id: values.tax_id,
      sections_id: values.sections_id,
      color,
      shop_id: shopId,
    };

    let savedProduct;
    try {
      savedProduct = isEditing
        ? await Put({
            request: `/products/${product.id}?shop_id=${shopId}&exercise_id=${exerciseId}&order=desc`,
            data: productData,
          })
        : await Post({
            request: `/products?shop_id=${shopId}&exercise_id=${exerciseId}`,
            data: productData,
          });

      if (!savedProduct?.id) {
        throw new Error(describeSaveError(savedProduct));
      }
    } catch (error) {
      toast.error(error.message || "No se pudo guardar el producto");
      setSaving(false);
      return;
    }

    const warnings = [];

    if (imageFile) {
      try {
        const response = await uploadFile({
          request: `/products/${savedProduct.id}/image/main?shop_id=${shopId}&exercise_id=${exerciseId}`,
          file: imageFile,
        });
        if (!response?.success) throw new Error("No se pudo subir la imagen");
      } catch {
        warnings.push("El producto se guardó, pero la imagen no pudo actualizarse");
      }
    }

    try {
      await syncModifiers(savedProduct.id);
    } catch {
      warnings.push(
        "El producto se guardó, pero algún modificador no pudo actualizarse",
      );
    }

    refetch?.();
    setSaving(false);
    setOpen(false);
    toast.success(
      isEditing
        ? "Producto actualizado correctamente"
        : "Producto creado correctamente",
    );
    warnings.forEach((warning) => toast.error(warning));
  });

  function handleOpenChange(nextOpen) {
    if (saving) return;
    setOpen(nextOpen);
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        showClose={false}
        className="flex h-dvh max-h-dvh w-screen flex-col overflow-hidden rounded-none p-0 sm:rounded-none"
      >
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
          <header className="flex h-[4.5rem] shrink-0 items-center justify-between gap-3 border-b bg-white px-3 sm:px-6">
            <SheetClose asChild>
              <Button
                type="button"
                variant="ghost"
                disabled={saving}
                className="h-12 shrink-0 px-3 text-base sm:px-4"
              >
                <Icon icon="mdi:arrow-left" className="mr-2 text-2xl sm:hidden" />
                <span className="hidden sm:inline">Volver</span>
              </Button>
            </SheetClose>

            <div className="min-w-0 text-center">
              <SheetTitle className="truncate text-lg font-semibold sm:text-xl">
                {isEditing ? "Editar producto" : "Nuevo producto"}
              </SheetTitle>
              <SheetDescription className="hidden truncate text-xs text-neutral-500 sm:block">
                Completa la información y guarda los cambios
              </SheetDescription>
            </div>

            <Button
              type="submit"
              loading={saving}
              disabled={isLoading}
              className="h-12 min-w-32 shrink-0 px-4 text-base sm:min-w-40 sm:px-6"
            >
              {!saving && <Icon icon="mdi:content-save-outline" className="mr-2 text-xl" />}
              {saving ? "Guardando" : "Guardar"}
            </Button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto bg-neutral-50">
            {isLoading ? (
              <div className="flex h-full min-h-80 items-center justify-center gap-3 text-neutral-500">
                <Spinner />
                <span className="text-base font-medium">Preparando editor…</span>
              </div>
            ) : (
              <div className="mx-auto grid w-full max-w-6xl gap-6 p-4 md:grid-cols-[18rem_minmax(0,1fr)] md:p-6">
                <aside className="space-y-4 md:sticky md:top-0 md:self-start">
                  <section className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                        <Icon icon="mdi:image-outline" className="text-2xl" />
                      </span>
                      <div>
                        <h2 className="font-semibold text-neutral-900">Imagen</h2>
                        <p className="text-xs text-neutral-500">Opcional · JPG, PNG o WebP</p>
                      </div>
                    </div>

                    <label
                      htmlFor={`${formId}-image`}
                      className="group relative mt-4 flex aspect-square cursor-pointer touch-manipulation items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-neutral-200 bg-neutral-100 transition-colors hover:border-blue-300 active:scale-[0.99]"
                      style={{ backgroundColor: color }}
                    >
                      <input
                        ref={imageInputRef}
                        id={`${formId}-image`}
                        type="file"
                        className="sr-only"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleImageChange}
                      />
                      {imageSource ? (
                        <img
                          src={imageSource}
                          alt={productName || "Producto"}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center px-6 text-center text-neutral-500">
                          <Icon icon="mdi:image-plus-outline" className="text-6xl" />
                          <span className="mt-3 text-sm font-medium">Toca para elegir una imagen</span>
                        </div>
                      )}
                      <span className="absolute inset-x-0 bottom-0 flex h-14 items-center justify-center gap-2 bg-black/55 text-sm font-semibold text-white backdrop-blur-sm">
                        <Icon icon="mdi:camera-outline" className="text-xl" />
                        {imageSource ? "Cambiar imagen" : "Añadir imagen"}
                      </span>
                    </label>

                    <div className="mt-4 rounded-xl bg-neutral-50 p-4">
                      <div className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                        Vista previa
                      </div>
                      <div className="mt-2 flex items-end justify-between gap-3">
                        <div className="min-w-0 font-semibold text-neutral-900">
                          {productName || "Nombre del producto"}
                        </div>
                        <div className="shrink-0 font-bold text-neutral-900">
                          {previewPrice.toFixed(2)}€
                        </div>
                      </div>
                    </div>
                  </section>
                </aside>

                <div className="space-y-6">
                  <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                        <Icon icon="mdi:information-outline" className="text-2xl" />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900">Información</h2>
                        <p className="text-sm text-neutral-500">Datos principales del producto</p>
                      </div>
                    </div>

                    <div className="mt-6 grid gap-5 sm:grid-cols-2">
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor={`${formId}-name`} className="text-sm font-semibold">
                          Nombre
                        </Label>
                        <Input
                          id={`${formId}-name`}
                          autoFocus
                          autoComplete="off"
                          placeholder="Ej. Café con leche"
                          className="h-14 text-lg"
                          {...register("name", {
                            required: "Escribe un nombre",
                            // validate devuelve `true` para validar y una cadena
                            // para fallar (esa cadena es el mensaje). Antes
                            // devolvía el propio nombre, así que RHF lo tomaba
                            // por un error y lo pintaba en rojo bajo el input
                            // sin dejar nunca enviar el formulario.
                            validate: (value) =>
                              String(value || "").trim() ? true : "Escribe un nombre",
                          })}
                        />
                        {errors.name && (
                          <p className="text-sm font-medium text-red-500">{errors.name.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor={`${formId}-price`} className="text-sm font-semibold">
                          Precio
                        </Label>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-lg font-semibold text-neutral-400">
                            €
                          </span>
                          <Input
                            id={`${formId}-price`}
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            placeholder="0,00"
                            className="h-14 pl-10 text-lg font-semibold"
                            {...register("price", {
                              required: "Introduce un precio válido",
                              validate: (value) =>
                                Number.isFinite(Number(value)) && Number(value) >= 0
                                  ? true
                                  : "Introduce un precio válido",
                            })}
                          />
                        </div>
                        {errors.price && (
                          <p className="text-sm font-medium text-red-500">{errors.price.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Impuesto</Label>
                        <Select
                          value={selectedTaxId}
                          onValueChange={(value) => {
                            setValue("tax_id", value);
                            clearErrors("tax_id");
                          }}
                        >
                          <SelectTrigger className="h-14 text-base">
                            <SelectValue placeholder="Selecciona un impuesto" />
                          </SelectTrigger>
                          <SelectContent>
                            {taxes.map((tax) => (
                              <SelectItem key={tax.id} value={String(tax.id)} className="min-h-11 py-2">
                                {tax.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.tax_id && (
                          <p className="text-sm font-medium text-red-500">{errors.tax_id.message}</p>
                        )}
                      </div>

                      <div className="space-y-2 sm:col-span-2">
                        <Label className="text-sm font-semibold">Sección</Label>
                        <Select
                          value={selectedSectionId}
                          onValueChange={(value) => {
                            setValue("sections_id", value);
                            clearErrors("sections_id");
                          }}
                        >
                          <SelectTrigger className="h-14 text-base">
                            <SelectValue placeholder="Selecciona una sección" />
                          </SelectTrigger>
                          <SelectContent>
                            {sections.map((section) => (
                              <SelectItem key={section.id} value={String(section.id)} className="min-h-11 py-2">
                                {section.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.sections_id && (
                          <p className="text-sm font-medium text-red-500">{errors.sections_id.message}</p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm sm:p-6">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                        <Icon icon="mdi:tune-variant" className="text-2xl" />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-neutral-900">Personalización</h2>
                        <p className="text-sm text-neutral-500">Color y opciones del producto</p>
                      </div>
                    </div>

                    <div className="mt-6 space-y-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Color de fondo</Label>
                        <ColorPickerCustom value={color} onChange={setColor} />
                        <p className="text-xs text-neutral-500">
                          Se utiliza detrás de la imagen cuando el producto no tiene foto.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-sm font-semibold">Modificadores</Label>
                        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                          <Select
                            value={pendingModifier}
                            onValueChange={setPendingModifier}
                            disabled={!availableModifiers.length}
                          >
                            <SelectTrigger className="h-14 text-base">
                              <SelectValue
                                placeholder={
                                  availableModifiers.length
                                    ? "Selecciona un grupo de modificadores"
                                    : "No hay grupos disponibles"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {availableModifiers.map((modifier) => (
                                <SelectItem
                                  key={modifier.id}
                                  value={String(modifier.id)}
                                  className="min-h-11 py-2"
                                >
                                  {modifier.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!pendingModifier}
                            onClick={addModifier}
                            className="h-14 px-5 text-base"
                          >
                            <Icon icon="mdi:plus" className="mr-2 text-xl" />
                            Añadir
                          </Button>
                        </div>

                        {selectedModifiers.length ? (
                          <div className="grid gap-2 sm:grid-cols-2">
                            {selectedModifiers.map((modifier) => (
                              <div
                                key={modifier.collectionId}
                                className="flex min-h-14 items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 px-3"
                              >
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-violet-600 shadow-sm">
                                  <Icon icon="mdi-tune-variant" className="text-xl" />
                                </span>
                                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-800">
                                  {modifier.name}
                                </span>
                                <button
                                  type="button"
                                  aria-label={`Quitar ${modifier.name}`}
                                  onClick={() =>
                                    setSelectedModifiers((current) =>
                                      current.filter(
                                        (item) =>
                                          item.collectionId !== modifier.collectionId,
                                      ),
                                    )
                                  }
                                  className="flex h-11 w-11 shrink-0 touch-manipulation items-center justify-center rounded-xl text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-500 active:scale-95"
                                >
                                  <Icon icon="mdi:close" className="text-xl" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex min-h-14 items-center justify-center rounded-xl border border-dashed border-neutral-200 bg-neutral-50 px-4 text-sm text-neutral-400">
                            Este producto no tiene modificadores
                          </div>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            )}
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
